import { useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Numeric } from '@blade/ui-kit'
import { Trend } from '@root/entities/trend'
import { useTranslation } from '@root/shared/localization'

import type { WalletPositionModel } from '@root/entities/position'
import type { ClassNameProps } from '@root/shared/react'

type InstantTradeSwapPnlProps = {
  position: WalletPositionModel
} & ClassNameProps

export const InstantTradeSwapPnl: React.FC<InstantTradeSwapPnlProps> = ({
  className,
  position,
}) => {
  const { t } = useTranslation()
  const [pnl] = useAtom(position.unrealizedPnlUsd)
  const [positionSize] = useAtom(position.remainingSizeCurrency)

  const pnlUsdTrimmed = useMemo(
    () => BigNumber(pnl.pnlUsd).decimalPlaces(2),
    [pnl.pnlUsd],
  )

  if (positionSize.isZero()) {
    return undefined
  }

  return (
    <Root className={className}>
      {t('common.upnl')}:{' '}
      <Pnl data-trend={Trend.of(pnl.pnlPercentage)}>
        <Numeric
          preset="currency-amount-short"
          value={pnlUsdTrimmed}
          currency="$"
          forceSign
        />
        <span> (</span>
        <Numeric forceSign preset="percents" value={pnl.pnlPercentage} />%
        <span>)</span>
      </Pnl>
    </Root>
  )
}

const Root = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-11);
  white-space: nowrap;
`

const Pnl = styled.span`
  &[data-trend='up'] {
    color: var(--up-9);
  }
  &[data-trend='down'] {
    color: var(--down-9);
  }
  &[data-trend='neutral'] {
    color: var(--text-11);
  }
`
